import type { Logger } from "@expert/logging";

import { useSendExpertAssistMessage } from "@asurion-core/expert-workspace-timeline";
import { useFeatures, type WorkspaceFeatures } from "@expert/features";
import { useVerifiedCustomerStore } from "@expert/sales-tools";
import { useGuideManager } from "@expert/solve-tools";
import { useMemo } from "react";

import { env } from "@/config";
import { useTaskMdn } from "@/customer-panel/hooks/useTaskMdn";
import { useGetQueriesBySearchType, usePartnerConfig } from "@/partner/usePartnerConfig";
import { isVoiceTask, useActiveTask, usePartner } from "@/sdk";
import { userCache } from "@/shared-utils";
import { sidecarTabEventBus } from "@/sidecar/sidecarTabEventBus";

import { useAutomaticGuide } from "./useAutomaticGuide";
import { useGuideDeviceInfo } from "./useGuideDeviceInfo";
import { useGuideRecommendation } from "./useGuideRecommendation";

// Bring the AI Assist tab to the front when the expert clicks "Ask Assist" in a guide, so the response
// they just triggered is visible. The sidecar tab bus lives in the app, so the guide package (which
// can't import it) receives this as a callback.
const focusAssistTab = () => sidecarTabEventBus.emit("focus-tab", "ai-assist");

interface PlatformGuideManagerProps {
    isCustomerSession: boolean;
    sessionId: string;
    logger: Logger;
}

export function usePlatformGuideManager({
    isCustomerSession,
    sessionId,
    logger: loggerProp,
}: Readonly<PlatformGuideManagerProps>) {
    const partner = usePartner();
    const activeTask = useActiveTask();
    const activeVoiceTask = useMemo(() => (isVoiceTask(activeTask) ? activeTask : null), [activeTask]);
    const { solveGuidedFlows, gaiaPayloadProps } = usePartnerConfig();
    const {
        features: { customFlowSystemId, isSolveGuidedFlowsEnabled },
    } = useFeatures<WorkspaceFeatures>();
    const { employeeId } = userCache;

    const globalConfig = useMemo(
        () => ({
            customFlowSystemId: customFlowSystemId ?? "",
            widgetId: solveGuidedFlows?.widgetId ?? "",
        }),
        [customFlowSystemId, solveGuidedFlows?.widgetId],
    );

    const logger = useMemo(
        () =>
            loggerProp.child({
                globalConfig,
                module: "usePlatformGuideManager",
                partner,
                sessionId,
                supportTeam: "solve",
            }),
        [globalConfig, loggerProp, partner, sessionId],
    );

    const sendMessage = useSendExpertAssistMessage(logger, gaiaPayloadProps);
    const isCustomerVerificationComplete = useVerifiedCustomerStore(
        (state) => state.getVerifiedCustomer(sessionId).isCustomerVerificationComplete,
    );
    const ssoAccessToken = userCache.ssoAccessToken ?? "";
    const clientChannelId = useGetQueriesBySearchType("QueryFindAgreementsByMDN")?.clientChannelId;

    // Deliberately unfiltered. The partner blocklists exist to stop unusable values reaching the
    // partner notes API; the guides look agreements up by MDN, and blanking it would change which
    // guides launch. That belongs in its own change with its own review, not this fix.
    // See `session-summary/summaryDispatchGate.ts`.
    const taskMdn = useTaskMdn({
        blocklistedNumbers: undefined,
        blocklistedPrefixes: undefined,
    });

    useGuideDeviceInfo({ logger, sessionId, gaiaPayloadProps });

    const callSid = activeVoiceTask?.agentCallId;

    const { openGuide, closeGuide, isGuideOpen, ...guideManagerInfo } = useGuideManager({
        callSid,
        clientChannelId,
        globalConfig,
        logger,
        partner,
        sendMessage,
        onAskAssist: focusAssistTab,
        sessionId,
        ssoAccessToken,
        taskMdn,
        employeeId: employeeId ?? "",
        isSolveGuidedFlowsEnabled,
        apiBaseUrl: env.backendUrls.apiBaseUrl,
    });

    useAutomaticGuide({
        activeVoiceTask,
        isCustomerSession,
        isCustomerVerificationComplete,
        isGuideOpen,
        partner,
        closeGuide,
        openGuide,
        logger,
    });

    useGuideRecommendation({
        callSid,
        isGuideOpen,
        logger,
        openGuide,
        sessionId,
    });

    return { ...guideManagerInfo, isGuideOpen, closeGuide };
}
